import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay'
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause'
import { faBackward } from '@fortawesome/pro-solid-svg-icons/faBackward'
import { faForward } from '@fortawesome/pro-solid-svg-icons/faForward'

import VolumeControl from './VolumeControl'
import Slider from './Slider'

const audioPlayerObjCache = new Map<string, HTMLAudioElement>()

enum AUDIO_READY_STATES {
  'HAVE_NOTHING' = 0,
  'HAVE_METADATA' = 1,
  'HAVE_CURRENT_DATA' = 2,
  'HAVE_FUTURE_DATA' = 3,
  'HAVE_ENOUGH_DATA' = 4,
}

const secondToMinute = (sec: number): string => {
  if (isNaN(sec)) {
    return '-:-'
  } else if (sec === 0) {
    return '00:00'
  }

  const minOut = Math.floor(sec / 60)
  const secOut = Math.round(sec % 60)
  return `${minOut < 10 ? '0' + minOut : minOut}:${secOut < 10 ? '0' + secOut : secOut}`
}

interface Props {
  src?: string
}

const AudioPlayer: React.FC<Props> = ({ src }) => {
  const playerRef = useRef<HTMLAudioElement>(new Audio())
  const stepSecond = 15

  const [fallbackMessage, setFallbackMessage] = useState<string | null>('Initializing...')
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [buffered, setBuffered] = useState(0)
  const [duration, setDuration] = useState(playerRef.current?.duration)

  useEffect(() => {
    if (playerRef.current.readyState >= AUDIO_READY_STATES.HAVE_CURRENT_DATA) {
      setDuration(Math.round(playerRef.current.duration))
    }
  }, [playerRef.current.readyState])

  useEffect(() => {
    if (!src) {
      setFallbackMessage('Audio recording is not available')
      return
    }

    let player = audioPlayerObjCache.get(src)

    if (!player) {
      player = new Audio(src)
      player.load()
      audioPlayerObjCache.set(src, player)
    }
    playerRef.current = player

    if (player.readyState === AUDIO_READY_STATES.HAVE_NOTHING) {
      player.load()
    }

    if (player.readyState >= AUDIO_READY_STATES.HAVE_CURRENT_DATA) {
      setDuration(Math.round(player.duration))
      setFallbackMessage(null)
    } else {
      setFallbackMessage('Initializing...')
    }

    const handleLoadedData = () => {
      setFallbackMessage(null)
    }

    const handleTimeUpdate = () => {
      setCurrentTime(Math.round(player?.currentTime ?? 0))
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    const handleProgress = () => {
      if (player?.buffered.length) {
        setBuffered(Math.round(player.buffered.end(0)))
      }
    }

    player.addEventListener('loadeddata', handleLoadedData)
    player.addEventListener('timeupdate', handleTimeUpdate)
    player.addEventListener('ended', handleEnded)
    player.addEventListener('progress', handleProgress)

    return () => {
      const player = playerRef.current
      if (!player) return

      player.pause()
      player.removeEventListener('loadeddata', handleLoadedData)
      player.removeEventListener('timeupdate', handleTimeUpdate)
      player.removeEventListener('ended', handleEnded)
      player.removeEventListener('progress', handleProgress)
    }
  }, [src])

  const togglePlay = () => {
    const player = playerRef.current
    if (!player) return
    if (isPlaying) {
      player.pause()
      setIsPlaying(false)
      return
    }

    player.ended && setCurrentTime(0)
    player.play()
    setIsPlaying(true)
  }

  const handleBackward = () => {
    const player = playerRef.current
    if (!player) return
    const updatedTime = currentTime - stepSecond
    setCurrentTime(updatedTime)
    player.currentTime = updatedTime
  }

  const handleForward = () => {
    const player = playerRef.current
    if (!player) return
    const updatedTime = player.currentTime + stepSecond
    setCurrentTime(updatedTime)
    player.currentTime = updatedTime
  }

  const seekerChange = (value: number) => {
    const player = playerRef.current
    if (!player) return
    setCurrentTime(value)
    player.currentTime = value
  }

  const volumeChange = (value: number) => {
    const player = playerRef.current
    if (!player) return
    player.volume = Math.round(value / 100)
  }

  const handleAudioPlayerClick = (e: React.MouseEvent<HTMLElement>) => {
    if (src) {
      e.stopPropagation()
      return
    }
  }

  return (
    <Container onClick={handleAudioPlayerClick}>
      {fallbackMessage && <Fallback>{fallbackMessage}</Fallback>}
      <FlexBox>
        <Controls>
          <button onClick={handleBackward}>
            <FontAwesomeIcon icon={faBackward} color="white" />
          </button>
          <button onClick={togglePlay}>
            {isPlaying ? (
              <FontAwesomeIcon icon={faPause} color="white" />
            ) : (
              <FontAwesomeIcon icon={faPlay} color="white" />
            )}
          </button>
          <button onClick={handleForward}>
            <FontAwesomeIcon icon={faForward} color="white" />
          </button>
        </Controls>
        <SeekerWrapper>
          <TimeText>{secondToMinute(currentTime)}</TimeText>
          <Slider max={duration} min={0} value={currentTime} buffered={buffered} onChange={seekerChange} />
          <TimeText>{secondToMinute(duration)}</TimeText>
        </SeekerWrapper>
        <VolumeControl onChange={volumeChange} disabled={!src} />
      </FlexBox>
    </Container>
  )
}

const Fallback: React.FC = (props) => (
  <FallbackContainer>
    <FallbackTextWrapper>{props.children}</FallbackTextWrapper>
  </FallbackContainer>
)

const Container = styled.div`
  display: block;
  width: 100%;
`

const FlexBox = styled.div`
  box-sizing: border-box;
  height: 45px;
  padding: 5px 15px 5px 10px;
  border-radius: 6px;
  background: linear-gradient(180deg, #6f6f6f 0%, #424242 100%);
  display: flex;
  align-items: center;
  width: 100%;
  overflow: visible;
`

const Controls = styled.div`
  display: flex;
`

const SeekerWrapper = styled.div(
  (p) => css`
    display: flex;
    width: 100%;

    & > * {
      color: ${p.theme.colors.white};
    }
  `,
)

const TimeText = styled.span`
  font-size: 14px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  margin: 0 15px;
`

const FallbackContainer = styled.div`
  box-sizing: border-box;
  height: 0;
  width: 100%;
  position: relative;
`

const FallbackTextWrapper = styled.div`
  position: absolute;
  box-sizing: border-box;
  height: 45px;
  width: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(p) => p.theme.colors.gray4};
  border-radius: 6px;
  z-index: 1;
  white-space: nowrap;
  overflow: hidden;
`

export default AudioPlayer
